import { useApi } from "@/hooks";
import { AppContext } from "@Context/common/general/context";
import { trainingPlansEvents } from "@Context/game/training-plans/training-plans-events";
import { buildTrainingPlanModel } from "@hooks/game/training-plans/training-plans-model";
import { ITrainingPlanProgressData, isNoActiveEnrollmentError, storeCompletedSessionsCount } from "@hooks/game/training-plans/training-plans-shared";
import { Api } from "@Shared/schemas";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

export interface IUseTrainingPlansProgressReturn {
  canUseTrainingPlans: boolean;
  isProgressResolved: boolean;
  progressData: ITrainingPlanProgressData | null;
  progressError: unknown | null;
  trainingPlanModel: ReturnType<typeof buildTrainingPlanModel>;
  refreshProgress: () => Promise<ITrainingPlanProgressData | null>;
}

/**
 * Shared progress state manager for Training Plans.
 *
 * Responsibility:
 * - Resolves training plan availability from the authenticated app state.
 * - Fetches and stores the current user's training plan progress lifecycle.
 * - Reacts to global training-plan refresh events and account/user changes.
 */
export const useTrainingPlansProgress = () => {
  const { state } = useContext(AppContext);
  const [progressData, setProgressData] = useState<ITrainingPlanProgressData | null>(null);
  const [progressError, setProgressError] = useState<unknown | null>(null);
  const [isProgressResolved, setIsProgressResolved] = useState(false);

  const { call: getUserPlanProgress } = useApi<typeof Api.TrainingPlans.GetUserPlanProgress>({
    api: Api.TrainingPlans.GetUserPlanProgress,
    disableLoader: true,
  });

  const isAuthenticated = state.user.authenticated;
  const hasSelectedAccount = Boolean(state.account.id);
  const hasSubscription = state.user.hasAccessSubscriptions && !state.user.hasAllAccessExpired;
  const canUseTrainingPlans = isAuthenticated && hasSelectedAccount && hasSubscription;

  const refreshProgress = useCallback(async () => {
    if (!canUseTrainingPlans) {
      setProgressData(null);
      setProgressError(null);
      setIsProgressResolved(false);
      return null;
    }

    try {
      setProgressError(null);
      const nextProgress = ((await getUserPlanProgress({ include_history: false })) ?? null) as ITrainingPlanProgressData | null;

      storeCompletedSessionsCount(
        {
          accountId: state.account.id,
          userId: state.user.id,
        },
        nextProgress
      );
      setProgressData(nextProgress);
      setIsProgressResolved(true);
      return nextProgress;
    } catch (error) {
      if (isNoActiveEnrollmentError(error)) {
        storeCompletedSessionsCount(
          {
            accountId: state.account.id,
            userId: state.user.id,
          },
          null
        );
        setProgressData(null);
        setProgressError(null);
        setIsProgressResolved(true);
        return null;
      }

      setProgressData(null);
      setProgressError(error);
      setIsProgressResolved(true);
      console.error("Could not fetch user training plan progress", error);
      throw error;
    }
  }, [canUseTrainingPlans, getUserPlanProgress, state.account.id, state.user.id]);

  const trainingPlanModel = useMemo(() => {
    return buildTrainingPlanModel(progressData);
  }, [progressData]);

  useEffect(() => {
    setProgressData(null);
    setProgressError(null);
    setIsProgressResolved(false);

    if (canUseTrainingPlans) {
      void refreshProgress().catch(() => undefined);
    }
  }, [canUseTrainingPlans, refreshProgress, state.account.id, state.user.id]);

  useEffect(() => {
    return trainingPlansEvents.onRefreshProgress(() => {
      void refreshProgress().catch(() => undefined);
    });
  }, [refreshProgress]);

  return {
    canUseTrainingPlans,
    isProgressResolved,
    progressData,
    progressError,
    refreshProgress,
    trainingPlanModel,
  } satisfies IUseTrainingPlansProgressReturn;
};
